/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The abstract class of AOMGH5ClickPlugin
 */

import { IContext } from 'com/didichuxing/context/IContext';
import { IClickFilter } from 'com/didichuxing/tracker/IClickFilter';
import { ITracker } from 'com/didichuxing/tracker/ITracker';
import { ITrackerConfiguration } from 'com/didichuxing/tracker/config/ITrackerConfiguration';
import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventArgs } from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import AReportingStrategyPlugin from 'com/didichuxing/tracker/plugin/AReportingStrategyPlugin';
import OMGH5ClickEvent, { ITargetPosition } from 'com/didichuxing/tracker/h5/plugins/events/OMGH5ClickEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';

interface IAOPHandler {

    /**
     * aop句柄，用以移除A该AOP的作用。
     */
    remove(): void;
}

export interface IClickedEventArgs extends ITrackedEventArgs {

    /**
     * 目标点击元素对象。
     */
    readonly target: Element;

    /**
     * @readonly
     * 点击的页面元素坐标。
     */
    readonly targetPosition: ITargetPosition;
}

export default abstract class AOMGH5ClickPlugin extends AReportingStrategyPlugin {

    /**
     * AOP handler list
     */
    private aopHandlers: IAOPHandler[] = [];

    /**
     * @override
     * @returns {string}
     */
    public getName(): string {
        return PluginUtils.PLUGIN_NAME_CLICK;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public install(): boolean {
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        return super.install();
    }

    /**
     * @override
     * @inheritDoc
     * @returns {ITrackerConfiguration}
     */
    protected get initialzedContext(): IContext {
        const context: ITrackerContext = this.getContext() as ITrackerContext;
        return {
            ...super.initialzedContext,
            autoClick: typeof context.autoClick === 'boolean' ? context.autoClick : true
        } as IContext;
    }

    /**
     * @override
     * @inheritDoc
     */
    public execute(): void {
        if ((this.getContext() as ITrackerContext).autoClick) { // 开启自动上报点击埋点。
            window.document.addEventListener('click', this.handleDocumentClick, true);
        }
    }

    /**
     * @override
     * @inheritDoc
     */
    public exit(): void {
        if ((this.getContext() as ITrackerContext).autoClick) { // 开启自动上报点击埋点。
            window.document.removeEventListener('click', this.handleDocumentClick, true);
        }
    }

    /**
     * classes是否有一向存在classList中。
     * @param {DOMTokenList} classList
     * @param {string[]} classes
     * @returns {boolean}
     */
    private containClasses(targetClassList: DOMTokenList, classes: string[]): boolean {
        for (let i: number = 0, j: number = classes.length; i < j; ++i) {
            if (targetClassList.contains(classes[i])) {
                return true;
            }
        }
        return false;
    }

    /**
     * 文档内所有元素点击事件代理。
     * 该代理采用捕获方式进行。
     * @override
     * @inheritDoc
     */
    protected handleDocumentClick(evt: MouseEvent): void {
        const filter: IClickFilter | undefined = this.getTracker().getClickedFilter();
        const eventTarget: EventTarget = evt.target as EventTarget;
        let target: Element;
        if (eventTarget.hasOwnProperty('correspondingUseElement')) { // SVG Element
            target = (eventTarget as SVGElementInstance).correspondingUseElement;
        } else {
            target = eventTarget as Element;
        }
        // 未命中过滤器则不上报
        if (filter && filter.class && !this.containClasses(target.classList, [].concat(filter.class as any))) {
            return;
        }
        this.report({
            eventId: PluginUtils.EVENT_ID_CLICK,
            attrs: {},
            target: target,
            targetPosition: evt
        } as IClickedEventArgs);
    }

    /**
     * @override
     * @inheritDoc
     * @param {ITrackedEventArgs} eventArgs
     * @returns {ITrackedEvent}
     */
    protected generateEvent(eventArgs: ITrackedEventArgs): ITrackedEvent {
        const tracker: ITracker = this.getTracker();
        return new OMGH5ClickEvent(eventArgs.attrs, this.getContext(), {
            target: (eventArgs as IClickedEventArgs).target,
            targetPosition: (eventArgs as IClickedEventArgs).targetPosition,
            clickedCover: tracker.getClickedCover(),
            clickedCoverisChild: tracker.getClickedCoverChild(),
            clickedFilter: tracker.getClickedFilter()
        });
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public uninstall(): boolean {
        while (this.aopHandlers.length) {
            const aopHandler: IAOPHandler | undefined = this.aopHandlers.pop();
            if (aopHandler) {
                aopHandler.remove();
            }
        }
        return super.uninstall();
    }
}
